import { Text, useTranslator } from '@eo-locale/react';
import { FC, useMemo, useEffect } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { PaymentsApi, UserApi } from '../api';
import { Button, Money, PaymentPage, Select } from '../components';
import { useContributions } from '../hooks';
import { useQuery } from 'react-query';

interface FormFields {
  id: string;
}

export const CheckoutPage: FC = () => {
  const t = useTranslator();

  const contributions = useContributions();
  const list = Array.isArray(contributions) ? contributions : [];

  const { data: user } = useQuery('user', UserApi.fetch);
  const hasPending = Boolean(user?.meta?.has_pending_current_year);
  const hasPaidCurrentYear = Boolean(user?.meta?.has_paid_current_year);

  const options = useMemo(
    () => list.map((c: any) => ({
      value: String(c.id),
      label: `${c.name}`,
      meta: c,
    })),
    [list],
  );

  const form = useForm<FormFields>();
  const id = form.watch('id');

  useEffect(() => {
    if (!id && options.length > 0) {
      form.setValue('id', options[0].value, { shouldValidate: true, shouldDirty: false });
    }
  }, [id, options, form]);

  const selected = useMemo(
    () => options.find((o) => o.value === id)?.meta,
    [options, id],
  );

  const onSubmit: SubmitHandler<FormFields> = async (values) => {
    if (!values.id || hasPending) return;
    const { redirect_url } = await PaymentsApi.pay(values.id);
    window.location.href = redirect_url;
  };

  const pendingBanner = hasPending ? (
    <div className="rounded-md bg-blue-50 border border-blue-200 p-3 mb-2">
      <span className="text-blue-800"><Text id="checkout:pending" /></span>
    </div>
  ) : null;

  const allPaidView = (
    <>
      <div className="rounded-md bg-green-50 border border-green-200 p-3 mb-4">
        <span className="text-green-800">
          <Text id="checkout:all_paid" />
        </span>
      </div>
      <Button theme="secondary" onClick={() => window.history.back()}>
        <Text id="payment_successful:action" />
      </Button>
    </>
  );

  const formView = (
    <FormProvider {...form}>
      <form className="flex flex-col gap-2" onSubmit={form.handleSubmit(onSubmit)}>
        <div>
          <label className="block text-sm mb-1">
            <Text id="checkout:membership" />
          </label>
          <Select
            disabled={hasPending || options.length === 0}
            placeholder={t.translate('checkout:membership')}
            options={options}
            {...form.register('id', { required: true })}
          />
        </div>

        {selected && (
          <div className="mt-0">
            <div className="text-center text-[15px] md:text-base font-semibold tracking-wide text-slate-700 mb-2">
              Wybrana składka
            </div>

            <div className="rounded border border-slate-200 p-3">
              <div className="font-medium">{selected.name}</div>

              {selected.description && (
                <div className="text-sm text-slate-600">
                  {selected.description}
                </div>
              )}

              {(selected.active_from || selected.active_to) && (
                <div className="text-sm text-slate-600">
                  <Text id="contribution:validity" />:{' '}
                  {selected.active_from ? selected.active_from : '—'} – {selected.active_to ? selected.active_to : '—'}
                </div>
              )}


              {/*{selected.year && (*/}
              {/*  <div className="text-sm text-slate-600">*/}
              {/*    <Text id="contribution:year" />: {selected.year}*/}
              {/*  </div>*/}
              {/*)}*/}
              <div className="mt-2">
                <span className="mr-2"><Text id="checkout:price" /></span>
                <Money amount={selected.value} />
              </div>
            </div>
          </div>
        )}


        <div className="flex items-center justify-center gap-2">
          <Button className="px-2.5" type="submit" disabled={hasPending || !id}>
            {hasPending ? <Text id="checkout:pending_action" /> : <Text id="checkout:submit" />}
          </Button>

          {hasPaidCurrentYear && (
            <span className="text-sm text-slate-600">
              <Text id="checkout:current_paid" />
            </span>
          )}
        </div>
      </form>
    </FormProvider>
  );

  const content = (options.length === 0 && !hasPending)
    ? allPaidView
    : (<>{pendingBanner}{formView}</>);

  return (
    <PaymentPage title="checkout:title" subtitle="checkout:helper">
      {content}
    </PaymentPage>
  );
};